'use es6';

import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import getLang from 'LearningCenterUI/utils/getLang';
import UIAlert from 'UIComponents/alert/UIAlert';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIFlex from 'UIComponents/layout/UIFlex';
import LearningPageHeader from './LearningPageHeader';
import TrackBody from './TrackBody';
import { fetchTrackByIdIfNeeded } from '../../actions/TrackActions';
import { trackIsLoading, getTrack } from '../../selectors/trackSelectors';
import TrackNavigatorContainer from '../Navigator/TrackNavigator/TrackNavigatorContainer';
import LearningPageSidebar from './LearningPageSidebar';
import LearningPageMainSection from './LearningPageMainSection';
import styled from 'styled-components';
import { getSelectedLanguageFiltersFromRoute } from '../../selectors/routeSelectors';
import { getContentById } from '../../selectors/contentSelectors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isTrackDeprecationWarning } from '../../utils/utils';
import TrackDeprecatedDescription from '../../components/TrackCard/TrackDeprecatedDescription';
import { useAvailableLanguageOrFallback } from '../../features/contentLanguage';
import useWootricSurvey from '../../hooks/useWootricSurvey';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StickyDiv = styled.div.withConfig({
  displayName: "TrackPageRouter__StickyDiv",
  componentId: "sc-1okt6v-0"
})(["position:sticky;top:49px;z-index:3;"]);
const TrackPageRouter = ({
  content,
  isLoading,
  fetchTrack,
  language,
  match,
  track
}) => {
  const {
    params: {
      trackId,
      lessonId,
      contentId
    }
  } = match;
  const shouldSkipSurvey = lessonId === 'exam';
  useWootricSurvey('CSAT', shouldSkipSurvey);
  const ContentTitle = content && content.get('name');
  const {
    languageToLoad,
    availableLanguagesLoading
  } = useAvailableLanguageOrFallback({
    trackId,
    language
  });
  useEffect(() => {
    if (!availableLanguagesLoading) fetchTrack(trackId, languageToLoad);
  }, [trackId, fetchTrack, languageToLoad, availableLanguagesLoading]);
  const isLoadingAndNoTrackData = (isLoading || availableLanguagesLoading) && (!track || track.get('language', '').toUpperCase() !== language);
  const deprecationWarningDate = track && track.get('deprecationWarningDate');
  const deprecationFinalDate = track && track.get('deprecationFinalDate');
  const deprecationMessage = track && track.get('deprecationMessage');
  const deprecationCta = track && track.get('deprecationCta');
  const deprecated = track && track.get('deprecated');
  const showWarning = isTrackDeprecationWarning(deprecated, deprecationFinalDate, deprecationWarningDate);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs(StickyDiv, {
      className: "track-page-sticky-div",
      children: [showWarning && !!deprecationMessage && /*#__PURE__*/_jsx(UIAlert, {
        "data-test-id": "track-deprecated-warning-alert",
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.deprecations.deprecationWarnTitle"
        }),
        type: "info",
        children: /*#__PURE__*/_jsx(TrackDeprecatedDescription, {
          compressed: true,
          cta: deprecationCta,
          message: deprecationMessage,
          trackId: trackId
        })
      }), !isLoadingAndNoTrackData && /*#__PURE__*/_jsx(LearningPageHeader, {
        ContentName: content && content.get('quizType') ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.lessonNavigator.quiz"
        }) : ContentTitle,
        defaultBackLink: {
          pathname: '/'
        },
        language: language,
        trackId: trackId
      })]
    }), isLoadingAndNoTrackData ? /*#__PURE__*/_jsx(UILoadingSpinner, {
      grow: true,
      minHeight: 400
    }) : /*#__PURE__*/_jsxs(UIFlex, {
      children: [/*#__PURE__*/_jsx(LearningPageSidebar, {
        id: "sidebarNavigator",
        withSubNav: 1,
        children: /*#__PURE__*/_jsx(TrackNavigatorContainer, {})
      }), /*#__PURE__*/_jsx(LearningPageMainSection, {
        children: /*#__PURE__*/_jsx(TrackBody, {
          contentId: contentId,
          language: language,
          lessonId: lessonId,
          trackId: trackId
        })
      })]
    })]
  });
};
TrackPageRouter.propTypes = {
  content: PropTypes.object,
  fetchRecommendedTracks: PropTypes.func,
  fetchTrack: PropTypes.func,
  isLastLessonInTrack: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  language: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({
      contentId: PropTypes.string,
      lessonId: PropTypes.string,
      trackId: PropTypes.string
    }).isRequired
  }).isRequired,
  track: ImmutablePropTypes.map
};
const mapStateToProps = () => {
  return (state, ownProps) => {
    const {
      match: {
        params: {
          trackId,
          contentId
        }
      }
    } = ownProps;
    const selectedTrack = getTrack(state, {
      trackId
    });
    const content = getContentById(state, contentId);
    const selectedTrackLanguage = selectedTrack ? selectedTrack.get('language') || getLang() : getLang();
    return {
      content,
      isLoading: trackIsLoading(state, {
        trackId
      }),
      track: selectedTrack,
      language: getSelectedLanguageFiltersFromRoute(state, ownProps).first() || selectedTrackLanguage
    };
  };
};
const mapDispatchToProps = dispatch => {
  return bindActionCreators({
    fetchTrack: fetchTrackByIdIfNeeded
  }, dispatch);
};
export default connect(mapStateToProps, mapDispatchToProps)(TrackPageRouter);